import { jsPDF } from "jspdf";
import { getSelectedDataList, applyPriceCode } from "../helpers/barcodeHelper";
import { _Api } from "components/api/_call";
import { formatWeightValue, getPrintPieceCount } from "./printQuantity";



/**
 * JW GIS RFID user-memory encoding (paired with RfidAgentService StockIdDecoder).
 *
 * Output: 32 hex nibbles = [leading zero nibbles][ASCII bytes as hex][trailing zero nibbles]
 * Printer: prepend "FD" when writing via ZPL ^RFW (34 nibbles total on wire).
 */
const RFID_ENCODE_FIELD_NIBBLES = 32;
const RFID_MAX_ASCII_CHARS = RFID_ENCODE_FIELD_NIBBLES / 2;

function encodeToHex(stockId) {
  const str = String(stockId).trim();
  if (!/^[A-Za-z0-9]{1,16}$/.test(str)) {
    throw new Error(`stock_id must be 1-${RFID_MAX_ASCII_CHARS} alphanumeric characters`);
  }
  const asciiHex = str
    .split('')
    .map((ch) => ch.charCodeAt(0).toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase();
  const characterCount = str.length;
  return asciiHex
    .padStart(RFID_ENCODE_FIELD_NIBBLES - characterCount, '0')
    .padEnd(RFID_ENCODE_FIELD_NIBBLES, '0')
    .toUpperCase();
}



// QR byte-mode capacity (bytes) per version at error-correction level L (~7%
// recovery, lowest overhead - swapped from M after comparing against a
// working ERP-generated QR: same physical space fits more data at L). Used to
// pick the smallest QR version that fits the "offline" payload, so ^BQ's
// magnification can be sized to the data instead of left fixed.
const QR_BYTE_CAPACITY_LEVEL_L = [
  17, 32, 53, 78, 106, 134, 154, 192, 230, 271,
  321, 367, 425, 458, 520, 586, 644, 718, 792, 858,
  929, 1003, 1091, 1171, 1273, 1367, 1465, 1528, 1628, 1732,
  1840, 1952, 2068, 2188, 2303, 2431, 2563, 2699, 2809, 2953,
];
const QR_BYTE_CAPACITY_LEVEL_M = [
  14, 26, 42, 62, 84, 106, 122, 152, 180, 213,
  251, 287, 331, 362, 412, 450, 504, 560, 624, 666,
  711, 779, 857, 911, 997, 1059, 1125, 1190, 1264, 1370,
  1452, 1538, 1628, 1722, 1839, 1929, 2049, 2185, 2323, 2427,
];
const QR_BYTE_CAPACITY_LEVEL_Q = [
  11, 20, 32, 46, 60, 74, 86, 108, 130, 151,
  177, 203, 241, 258, 292, 322, 364, 394, 442, 482,
  509, 565, 611, 661, 715, 751, 805, 868, 908, 982,
  1030, 1112, 1168, 1228, 1283, 1351, 1431, 1531, 1631, 1735,
];
const QR_BYTE_CAPACITY_LEVEL_H = [
  7, 14, 24, 34, 44, 58, 64, 84, 98, 119,
  137, 155, 177, 194, 220, 250, 280, 310, 338, 382,
  403, 439, 461, 511, 535, 593, 625, 658, 698, 742,
  790, 842, 898, 958, 983, 1051, 1093, 1139, 1219, 1273,
];
const QR_BYTE_CAPACITY_BY_ERROR_CORRECTION = {
  L: QR_BYTE_CAPACITY_LEVEL_L,
  M: QR_BYTE_CAPACITY_LEVEL_M,
  Q: QR_BYTE_CAPACITY_LEVEL_Q,
  H: QR_BYTE_CAPACITY_LEVEL_H,
};
// Printer is confirmed 300dpi.
const PRINT_DOTS_PER_MM = 300 / 25.4;
const QR_TARGET_SIZE_MM = 9;
// The offline QR sits between Y=230 and the ^LL354 label edge - nominally 124
// dots (~10.5mm), but two rounds of real prints still clipped at magnifications
// the 124 figure said should fit (99 dots used of "124 available"). Something
// in this model is off (quiet-zone margin not accounted for, real available
// space smaller than assumed, etc.) and isn't isolated yet, so knocked this
// down to a deliberately conservative 95 dots until a real measurement pins
// down the actual number - err on "too small to scan" over "clips again".
// Confirmed safe (scanned fine, no clip) at 95 -> magnification 2 (~5.6mm),
// but that read as too small. Nudging up to just clear magnification 3
// (~8.4mm) as the next untested step, not jumping straight to a bigger one.
const QR_AVAILABLE_SPACE_DOTS = 105;
// Long payloads already require a larger QR version on this small tag. L keeps
// the symbol one version smaller than M while preserving the ERP field format.
const TEMPLATE3_OFFLINE_QR_ERROR_CORRECTION = "L";
// Use a field-hex LF so scanners receive a real line-feed character. The
// printable pipe remains available as a final fallback, but is not the default.
const TEMPLATE3_OFFLINE_QR_NEWLINE_MODE = "erp_lf";
// Short payloads previously scanned at magnification 4, while long payloads
// need the automatic size calculation to stay inside the label.
const TEMPLATE3_OFFLINE_QR_FORCE_MAGNIFICATION = true;
const TEMPLATE3_OFFLINE_QR_MAGNIFICATION = 4;
// Keep the known-good short-label path unchanged. Long L-level payloads get a
// larger module only when the extra space is available in the long layout.
const TEMPLATE3_OFFLINE_QR_LONG_PAYLOAD_THRESHOLD_BYTES = 78;
const TEMPLATE3_OFFLINE_QR_LONG_PAYLOAD_MAGNIFICATION = 3;
const TEMPLATE3_OFFLINE_QR_LONG_PAYLOAD_POSITION_Y = 214;
const TEMPLATE3_OFFLINE_QR_REMOVE_SPACES = true;
const TEMPLATE3_OFFLINE_QR_REMOVE_SPACES_LONG_ONLY = true;
const TEMPLATE3_OFFLINE_QR_USE_ALPHANUMERIC = true;
// Temporary test shortening for the QR payload only; remove after validation.
const TEMPLATE3_OFFLINE_QR_SHORTEN_WHITE_GOLD = false;
// Keep the full QR symbol inside ^LL354 for the long-payload magnification.
const TEMPLATE3_OFFLINE_QR_POSITION_Y = 230;
// Keep line boundaries, but remove alignment whitespace and an empty final line
// so a long payload does not unnecessarily move to a larger QR version.
const TEMPLATE3_OFFLINE_QR_COMPACT_PAYLOAD = true;
// ^BQ's magnification param is NOT a direct dots-per-module scale on this
// printer, and the ratio isn't constant across magnification values either:
// magnification 4 measured ~2.33 dots/module (~0.58/unit) via an old
// RfidTagPreview.js calibration, but a real CLEO-B1 print at magnification 5
// (41 modules) came out ~14mm (~0.81/unit) - noticeably steeper than a flat
// 0.58 would predict (would've given ~11.9mm). Using the more recent,
// directly-measured ratio; a magnification-4 data point would be needed to
// know whether this is truly linear or just curved between the two.
const QR_DOTS_PER_MODULE_PER_MAGNIFICATION = 0.8066;

export function estimateQrModuleCount(text, errorCorrection = "L") {
  const byteLength = new TextEncoder().encode(String(text || "")).length;
  const capacities = QR_BYTE_CAPACITY_BY_ERROR_CORRECTION[errorCorrection] || QR_BYTE_CAPACITY_LEVEL_L;
  const versionIndex = capacities.findIndex((capacity) => capacity >= byteLength);
  const version = versionIndex === -1 ? capacities.length : versionIndex + 1;

  return 21 + (version - 1) * 4;
}

function getQrMagnificationForTargetSize(text, targetSizeMM = QR_TARGET_SIZE_MM, errorCorrection = "L") {
  if (TEMPLATE3_OFFLINE_QR_FORCE_MAGNIFICATION) {
    return TEMPLATE3_OFFLINE_QR_MAGNIFICATION;
  }

  const byteLength = new TextEncoder().encode(String(text || "")).length;
  if (byteLength > TEMPLATE3_OFFLINE_QR_LONG_PAYLOAD_THRESHOLD_BYTES) {
    return TEMPLATE3_OFFLINE_QR_LONG_PAYLOAD_MAGNIFICATION;
  }

  const moduleCount = estimateQrModuleCount(text, errorCorrection);
  const dotsPerModuleNeeded = (targetSizeMM * PRINT_DOTS_PER_MM) / moduleCount;
  const dotsPerModuleAvailable = QR_AVAILABLE_SPACE_DOTS / moduleCount;
  // Integer magnification can't land exactly on the target size; round up rather
  // than to nearest, since an undersized QR risks not scanning at all while an
  // oversized one just costs label space - but never past what actually fits,
  // or the QR clips (the original bug this was built to fix).
  const wanted = Math.ceil(dotsPerModuleNeeded / QR_DOTS_PER_MODULE_PER_MAGNIFICATION);
  const maxFitting = Math.floor(dotsPerModuleAvailable / QR_DOTS_PER_MODULE_PER_MAGNIFICATION);

  return Math.min(10, Math.max(1, Math.min(wanted, maxFitting)));
}

function normalizeQrNewlinesForMode(text, newlineMode) {
  const normalizedText = String(text ?? "").replace(/\r\n?|\n/g, "\n");

  if (newlineMode === "erp_cr" || newlineMode === "erp_lf") {
    return normalizedText.replace(/\n/g, newlineMode === "erp_cr" ? "\r" : "\n");
  }

  if (newlineMode === "crlf") {
    return normalizedText.replace(/\n/g, "\r\n");
  }

  if (newlineMode === "pipe") {
    return normalizedText.replace(/\n/g, "|");
  }

  return normalizedText;
}

function prepareOfflineQrPayload(text) {
  const normalizedText = String(text ?? "").replace(/\r\n?|\n/g, "\n");
  const shortenedText = TEMPLATE3_OFFLINE_QR_SHORTEN_WHITE_GOLD
    ? normalizedText.replace(/\bwhite gold\b/gi, "WG")
    : normalizedText;

  if (!TEMPLATE3_OFFLINE_QR_COMPACT_PAYLOAD) {
    return shortenedText;
  }

  const isLongPayload = new TextEncoder().encode(shortenedText).length > TEMPLATE3_OFFLINE_QR_LONG_PAYLOAD_THRESHOLD_BYTES;
  const shouldRemoveSpaces = TEMPLATE3_OFFLINE_QR_REMOVE_SPACES &&
    (!TEMPLATE3_OFFLINE_QR_REMOVE_SPACES_LONG_ONLY || isLongPayload);
  const shouldUseAlphanumeric = TEMPLATE3_OFFLINE_QR_USE_ALPHANUMERIC && isLongPayload;

  return shortenedText
    .split("\n")
    .map((line) => {
      const compactLine = line.replace(/[ \t]+/g, " ").trim();
      const normalizedLine = shouldUseAlphanumeric ? compactLine.toUpperCase() : compactLine;
      return shouldRemoveSpaces ? normalizedLine.replace(/[ \t]/g, "") : normalizedLine;
    })
    .join("\n")
    .replace(/\n+$/, "");
}

// Encode the QR field while keeping line-break representation configurable.
// Escape the field according to the selected ^FH indicator.
function encodeQrFieldDataForZpl(text, newlineMode = TEMPLATE3_OFFLINE_QR_NEWLINE_MODE) {
  const normalizedText = String(text ?? "").replace(/\r\n?|\n/g, "\n");
  const usesErpEncoding = newlineMode === "erp_cr" || newlineMode === "erp_lf";
  const newlineValue = usesErpEncoding
    ? newlineMode === "erp_cr" ? "\\0D" : "\\0A"
    : newlineMode === "crlf"
      ? "_0D_0A"
      : "_0A";
  const payloadText = newlineMode === "pipe" ? normalizedText.replace(/\n/g, "|") : normalizedText;

  const escapedPayloadText = usesErpEncoding
    ? payloadText
      .replace(/\\/g, "\\5C")
      .replace(/_/g, "\\5F")
      .replace(/\^/g, "\\5E")
      .replace(/~/g, "\\7E")
    : payloadText
      .replace(/_/g, "_5F")
      .replace(/\^/g, "_5E")
      .replace(/~/g, "_7E");

  return escapedPayloadText
    .replace(/\n/g, newlineValue);
}

function checkType(value) {
  if (Array.isArray(value)) {
    return "Array";
  } else if (typeof value === "object" && value !== null) {
    return "Object";
  } else {
    return "Neither Object nor Array";
  }
}

// Stone weight (Cts) must show exactly 2 decimal places, truncated rather than
// rounded (0.025 -> "0.02", not "0.03") - string-sliced instead of
// multiply-by-100-then-floor to avoid float precision drift (e.g. 1.005*100
// landing on 100.49999999999999).
function truncateToTwoDecimals(value) {
  const str = String(value ?? "").trim();
  const dotIndex = str.indexOf(".");

  if (dotIndex === -1) {
    return str === "" ? str : str + ".00";
  }

  return str.slice(0, dotIndex + 3).padEnd(dotIndex + 3, "0");
}
// Field text prints via ^A0I (180deg). Under that orientation, increasing X moves
// the printed text further LEFT on the label, not right (confirmed empirically:
// raising the char-width compensation pushed short SKUs further right, the
// opposite of what a naive left-to-right model predicts). So keeping a value's
// left edge flush with a fixed point as its length shrinks below the fallback
// requires ADDING the shortfall back, the same convention getLeftColumnTextX
// already uses correctly for stock_id.
const getLeftAlignedTextX = (baseX, baseTextLength, averageCharWidth, value) => {
  const textLength = String(value || "").length;

  return baseX + ((baseTextLength - textLength) * averageCharWidth);
};

const METAL_BASE_X = 552;
const METAL_BASE_TEXT_LENGTH = "Metal".length;
const METAL_AVERAGE_CHAR_WIDTH = 5;
const METAL_MAX_DISPLAY_LENGTH = 5;
export const getMetalDisplayValue = (value) => {
  return String(value || "").substring(0, METAL_MAX_DISPLAY_LENGTH);
};
export const getMetalTextX = (value) => {
  return getLeftAlignedTextX(METAL_BASE_X, METAL_BASE_TEXT_LENGTH, METAL_AVERAGE_CHAR_WIDTH, value);
};

const WEIGHT_BASE_X = 552;
const WEIGHT_BASE_TEXT_LENGTH = "5.00 G".length;
const WEIGHT_AVERAGE_CHAR_WIDTH = 14;
export const getWeightTextX = (value) => {
  const textLength = String(value || "").length;

  return WEIGHT_BASE_X - ((textLength - WEIGHT_BASE_TEXT_LENGTH) * WEIGHT_AVERAGE_CHAR_WIDTH);
};

// Field text prints via ^A0I (180deg) at font size 27,27 for stock_id, price,
// and SKU alike. A flat per-character width can't stay flush across arbitrary
// values: font-0 glyphs are proportional, so a digit-heavy short string and a
// letter-heavy one of the same length render at different widths. Measure the
// real relative glyph width with jsPDF instead of assuming one, then convert mm
// to ZPL dots with a scale fit against real prints at this font size:
// "CLEO-B1" flush at X=664, "AVEN-B5YGDD-S" flush at X=~562, "RG026" flush at
// X=~704 (measured ~1mm right of a print at X=693), "RG025RGDD-53" flush at
// X=~594 (measured ~1 char right of a print at X=576).
const font27MeasureDoc = new jsPDF({ unit: "mm" });
font27MeasureDoc.setFont("helvetica", "normal");
font27MeasureDoc.setFontSize(10);
export const FONT27_DOTS_PER_MM = 7.528;

const LEFT_COLUMN_BASE_X = 676;
const LEFT_COLUMN_BASE_TEXT = "31,800.00";
export const getLeftColumnTextX = (value) => {
  const text = String(value || "");
  const widthDeltaMM = font27MeasureDoc.getTextWidth(LEFT_COLUMN_BASE_TEXT) - font27MeasureDoc.getTextWidth(text);

  return Math.round(LEFT_COLUMN_BASE_X + widthDeltaMM * FONT27_DOTS_PER_MM);
};
// -8 was tuned for the old flat-average getLeftColumnTextX; it undershoots now
// that getLeftColumnTextX measures real glyph width, confirmed by "157,500.00"
// (11 chars vs the "31,800.00" 9-char fallback) sitting ~1 char too far right.
const PRICE_TEXT_X_OFFSET = 5;
export const getPriceColumnTextX = (value) => {
  return getLeftColumnTextX(value) + PRICE_TEXT_X_OFFSET;
};

// The barcode's FO_x behaves like every other field on this label (confirmed
// empirically: lowering X from 550 to 505 for a 5-char value pushed it further
// RIGHT, not left), so despite ^BCN's "N" orientation param, larger X still
// means further left here. 550 was correct for a "full length" (7-char) value;
// shorter values render fewer Code128 bars and were left visually adrift from
// that same left edge. Calibrated against a real print: stock_id "00123"
// (5 chars, 2 short of the 7-char baseline) needed to shift ~3 char-widths
// left, i.e. ~22.5 dots per missing character.
export const BARCODE_LEFT_BASE_X = 550;
export const BARCODE_LEFT_BASELINE_LENGTH = 7;
export const BARCODE_LEFT_PER_CHAR_DOTS = 22.5;
export const getBarcodeTextX = (value) => {
  const textLength = String(value || "").length;
  const shortfall = Math.max(0, BARCODE_LEFT_BASELINE_LENGTH - textLength);

  return Math.round(BARCODE_LEFT_BASE_X + shortfall * BARCODE_LEFT_PER_CHAR_DOTS);
};

export const SKU_X_INTERCEPT = 782.366;
export const getSkuTextX = (value) => {
  const text = String(value || "");

  return Math.round(SKU_X_INTERCEPT - FONT27_DOTS_PER_MM * font27MeasureDoc.getTextWidth(text));
};

const TEMPLATE3_DOTS_PER_MM = 300 / 25.4;
const TEMPLATE3_DOTS_PER_POINT = 300 / 72;
const TEMPLATE3_LABEL_WIDTH_DOTS = 826;
const TEMPLATE3_LABEL_HEIGHT_DOTS = 354;
const TEMPLATE3_STONE_AREA_LEFT = 523;
const TEMPLATE3_STONE_AREA_RIGHT = 826;
const TEMPLATE3_STONE_AREA_TOP = 70;
const TEMPLATE3_STONE_AREA_BOTTOM = 205;
const TEMPLATE3_IMAGE_MAX_WIDTH_MM = 16;
const TEMPLATE3_IMAGE_MAX_HEIGHT_MM = 10;
const TEMPLATE3_DEFAULT_IMAGE_SCALE = 1.4;
const TEMPLATE3_IMAGE_X_OFFSET_DOTS = 64;
const isTemplate3OptionEnabled = (value) => (
  value === true
  || value === 1
  || value === "1"
  || String(value).toLowerCase() === "true"
);

const isTemplate3OptionDisabled = (value) => (
  value === false
  || value === 0
  || value === "0"
  || String(value).toLowerCase() === "false"
);

const getTemplate3PositionDots = (position, axis, fallback) => {
  const value = Number(position?.[axis]);
  return Number.isFinite(value) ? Math.round(value * TEMPLATE3_DOTS_PER_MM) : fallback;
};

const isTemplate3PositionLocked = (position) => (
  position?.position_locked === true
  || position?.position_locked === 1
  || position?.position_locked === "1"
  || String(position?.position_locked).toLowerCase() === "true"
);

const getTemplate3TextWidthDots = (text, fontSizeDots = 27) => {
  const normalizedText = String(text ?? "");
  if (!normalizedText) return 0;

  const fontSizePoints = Number(fontSizeDots) / TEMPLATE3_DOTS_PER_POINT;
  const widthMmAtTenPoints = font27MeasureDoc.getTextWidth(normalizedText);
  return Math.round(widthMmAtTenPoints * (fontSizePoints / 10) * TEMPLATE3_DOTS_PER_MM);
};

const getTemplate3FieldSize = (config, fieldName, fallback) => {
  const configuredSize = Number(config?.template3_field_positions?.[fieldName]?.size);
  return Number.isFinite(configuredSize) && configuredSize > 0
    ? Math.round(configuredSize)
    : fallback;
};

const TEMPLATE3_LOCK_REFERENCE_TEXT = {
  sku: "SB03218KYGDM-45",
  stock_id: "100",
  metal: "18KYG",
  weight: "5.00g",
  pcs: "3p",
  price: "31,800",
};

// ^A0I uses the field origin as the visual right side of the text. When a
// position is locked, keep the visual center from the saved sample instead of
// letting a longer or shorter real value drift away from the edited position.
const getTemplate3LockedX = (position, fieldName, text, baseX, fontSizeDots = 27) => {
  if (!isTemplate3PositionLocked(position)) return baseX;

  const referenceText = String(
    position?.lock_reference_text ?? TEMPLATE3_LOCK_REFERENCE_TEXT[fieldName] ?? "",
  );
  if (!referenceText || !String(text ?? "")) return baseX;

  const referenceWidth = getTemplate3TextWidthDots(referenceText, fontSizeDots);
  const currentWidth = getTemplate3TextWidthDots(text, fontSizeDots);
  return Math.round(baseX + ((currentWidth - referenceWidth) / 2));
};

const getTemplate3FieldPositionDots = (config, fieldName, fallbackX, fallbackY, text, fontSizeDots = 27) => {
  const fieldPositions = [
    config?.template3_field_positions,
    config?.profile_setup_info?.template3_field_positions,
    config?.config_check_info?.template3_field_positions,
  ].find((candidate) => candidate?.[fieldName]) || null;
  const position = fieldPositions?.configured !== false
    ? fieldPositions?.[fieldName]
    : null;
  const baseX = getTemplate3PositionDots(position, "x", fallbackX);

  return {
    x: getTemplate3LockedX(position, fieldName, text, baseX, fontSizeDots),
    y: getTemplate3PositionDots(position, "y", fallbackY),
  };
};

const appendTemplate3PieceLabel = (prnCommand, config, weightPosition, pieceCount) => {
  const pieceText = `${pieceCount}p`;
  const pieceSize = getTemplate3FieldSize(config, "pcs", 15);
  const pieceWidth = getTemplate3TextWidthDots(pieceText, pieceSize);
  const piecePosition = getTemplate3FieldPositionDots(
    config,
    "pcs",
    Math.max(0, weightPosition.x - pieceWidth - 4),
    weightPosition.y,
    pieceText,
    pieceSize,
  );

  return prnCommand
    + "^FO" + piecePosition.x + "," + piecePosition.y
    + "^A0I," + pieceSize + "," + pieceSize + "^FD" + pieceText + "^FS";
};

const getTemplate3ImageLayout = (imageConfig, sourceAspectRatioOverride = null) => {
  const configuredScale = Number(imageConfig?.scale);
  const imageScale = Number.isFinite(configuredScale)
    ? Math.min(2, Math.max(0.1, configuredScale))
    : TEMPLATE3_DEFAULT_IMAGE_SCALE;
  const requestedWidthMm = 8 * imageScale;
  const requestedHeightMm = 8 * imageScale;
  let width = Math.max(
    1,
    Math.min(
      TEMPLATE3_LABEL_WIDTH_DOTS,
      Math.round(Math.min(TEMPLATE3_IMAGE_MAX_WIDTH_MM, requestedWidthMm) * TEMPLATE3_DOTS_PER_MM),
    ),
  );
  let height = Math.max(
    1,
    Math.min(
      TEMPLATE3_LABEL_HEIGHT_DOTS,
      Math.round(Math.min(TEMPLATE3_IMAGE_MAX_HEIGHT_MM, requestedHeightMm) * TEMPLATE3_DOTS_PER_MM),
    ),
  );

  // Keep the complete uploaded logo visible. Scale controls the base image
  // box; it must not stretch or crop the source image's lower text.
  const sourceWidth = Number(imageConfig?.width);
  const sourceHeight = Number(imageConfig?.height);
  const sourceAspectRatio = sourceAspectRatioOverride || (sourceWidth > 0 && sourceHeight > 0
    ? sourceWidth / sourceHeight
    : null);
  if (sourceAspectRatio) {
    width = Math.max(1, Math.min(width, Math.round(height * sourceAspectRatio)));
    height = Math.max(1, Math.min(height, Math.round(width / sourceAspectRatio)));
  }
  // Template 3 does not use the normal HTML preview coordinates. Keep the
  // image centered in the RFID stone-list area so it cannot drift to the
  // opposite side when an old profile still has position { x: 1, y: 1 }.
  const centeredX = Math.max(
    TEMPLATE3_STONE_AREA_LEFT,
    Math.min(
      TEMPLATE3_STONE_AREA_RIGHT - width,
      Math.round(TEMPLATE3_STONE_AREA_LEFT + ((TEMPLATE3_STONE_AREA_RIGHT - TEMPLATE3_STONE_AREA_LEFT - width) / 2) + TEMPLATE3_IMAGE_X_OFFSET_DOTS),
    ),
  );
  const centeredY = Math.round(TEMPLATE3_STONE_AREA_TOP + ((TEMPLATE3_STONE_AREA_BOTTOM - TEMPLATE3_STONE_AREA_TOP - height) / 2));
  const configuredPosition = imageConfig?.template3_position_configured
    ? imageConfig.position
    : null;
  const configuredX = getTemplate3PositionDots(configuredPosition, "x", centeredX);
  const configuredY = getTemplate3PositionDots(configuredPosition, "y", centeredY);

  return {
    width,
    height,
    x: Math.max(0, Math.min(TEMPLATE3_LABEL_WIDTH_DOTS - width, configuredX)),
    y: Math.max(0, Math.min(TEMPLATE3_LABEL_HEIGHT_DOTS - height, configuredY)),
  };
};

const getTemplate3ImageCommand = (imageConfig) => new Promise((resolve) => {
  if (
    !imageConfig?.data
    || !imageConfig.data.startsWith("data:image/png;base64,")
    || typeof window === "undefined"
  ) {
    resolve("");
    return;
  }

  const image = new Image();
  image.onload = () => {
    try {
      const sourceWidth = image.naturalWidth || image.width;
      const sourceHeight = image.naturalHeight || image.height;
      const sourceCanvas = document.createElement("canvas");
      sourceCanvas.width = sourceWidth;
      sourceCanvas.height = sourceHeight;
      const sourceContext = sourceCanvas.getContext("2d", { willReadFrequently: true });
      if (!sourceContext) {
        resolve("");
        return;
      }
      sourceContext.fillStyle = "#fff";
      sourceContext.fillRect(0, 0, sourceWidth, sourceHeight);
      sourceContext.drawImage(image, 0, 0, sourceWidth, sourceHeight);

      // Remove transparent/white padding before fitting the logo into the
      // RFID field. This keeps small bottom marks such as "18K" in the fit.
      const sourcePixels = sourceContext.getImageData(0, 0, sourceWidth, sourceHeight).data;
      let minX = sourceWidth;
      let minY = sourceHeight;
      let maxX = -1;
      let maxY = -1;
      for (let sourceY = 0; sourceY < sourceHeight; sourceY += 1) {
        for (let sourceX = 0; sourceX < sourceWidth; sourceX += 1) {
          const sourceIndex = ((sourceY * sourceWidth) + sourceX) * 4;
          const gray = sourcePixels[sourceIndex];
          const alpha = sourcePixels[sourceIndex + 3];
          if (alpha > 0 && gray < 250) {
            minX = Math.min(minX, sourceX);
            minY = Math.min(minY, sourceY);
            maxX = Math.max(maxX, sourceX);
            maxY = Math.max(maxY, sourceY);
          }
        }
      }
      const hasContentBounds = maxX >= minX && maxY >= minY;
      const contentBounds = hasContentBounds
        ? {
            x: minX,
            y: minY,
            width: maxX - minX + 1,
            height: maxY - minY + 1,
          }
        : { x: 0, y: 0, width: sourceWidth, height: sourceHeight };
      const imageLayout = getTemplate3ImageLayout(
        imageConfig,
        contentBounds.width / contentBounds.height,
      );
      const canvas = document.createElement("canvas");
      canvas.width = imageLayout.width;
      canvas.height = imageLayout.height;
      const context = canvas.getContext("2d", { willReadFrequently: true });
      if (!context) {
        resolve("");
        return;
      }
      context.fillStyle = "#fff";
      context.fillRect(0, 0, imageLayout.width, imageLayout.height);
      context.save();
      context.translate(imageLayout.width, imageLayout.height);
      context.rotate(Math.PI);
      context.drawImage(
        sourceCanvas,
        contentBounds.x,
        contentBounds.y,
        contentBounds.width,
        contentBounds.height,
        0,
        0,
        imageLayout.width,
        imageLayout.height,
      );
      context.restore();

      const pixels = context.getImageData(0, 0, imageLayout.width, imageLayout.height).data;
      const rowBytes = Math.ceil(imageLayout.width / 8);
      let hex = "";

      for (let y = 0; y < imageLayout.height; y += 1) {
        for (let byteX = 0; byteX < rowBytes; byteX += 1) {
          let value = 0;
          for (let bit = 0; bit < 8; bit += 1) {
            const x = (byteX * 8) + bit;
            if (x >= imageLayout.width) continue;
            const pixelIndex = ((y * imageLayout.width) + x) * 4;
            const gray = pixels[pixelIndex];
            const alpha = pixels[pixelIndex + 3];
            // Ignore transparent pixels so they remain white on the label.
            if (alpha > 0 && gray < 180) value |= (1 << (7 - bit));
          }
          hex += value.toString(16).padStart(2, "0").toUpperCase();
        }
      }

      const totalBytes = rowBytes * imageLayout.height;
      resolve(`^FO${imageLayout.x},${imageLayout.y}^GFA,${totalBytes},${totalBytes},${rowBytes},${hex}^FS`);
    } catch (error) {
      console.warn("Unable to render RFID barcode profile image", error);
      resolve("");
    }
  };
  image.onerror = () => resolve("");
  image.src = imageConfig.data;
});

const escapeTemplate3Text = (value) => String(value ?? "")
  .replace(/[\^~]/g, " ")
  .trim();

const getTemplate3TextCommand = (config, imageConfig) => {
  const textOption = config?.text ?? config?.config_check_info?.text;
  if (isTemplate3OptionDisabled(textOption)) return "";

  const text = escapeTemplate3Text(
    config?.text_message
      ?? config?.config_check_info?.text_message,
  );
  if (!text) return "";

  const hasImage = Boolean(imageConfig?.data);
  const fontSize = Math.max(12, Math.min(100, Math.round((Number(config.text_font_size) || 6) * TEMPLATE3_DOTS_PER_POINT)));

  // ^A0I uses the inverted vertical direction of the RFID layout. A smaller
  // field Y places ordinary text visually below the graphic.
  // Use the same plain field command as the other Template 3 text fields.
  // With ^A0I, ^FO is the visual right edge of the text, so estimate the
  // rendered width and place that edge around the center of the RFID area.
  const estimatedTextWidth = Math.round(text.length * fontSize * 0.58);
  const centeredTextX = hasImage
    ? Math.min(
        TEMPLATE3_STONE_AREA_RIGHT,
        Math.max(
          TEMPLATE3_STONE_AREA_LEFT,
          Math.round(((TEMPLATE3_STONE_AREA_LEFT + TEMPLATE3_STONE_AREA_RIGHT) / 2) - (estimatedTextWidth / 2)),
        ),
      )
    : 760;
  const configuredTextX = Number(config?.text_config?.position?.x);
  const configuredTextY = Number(config?.text_config?.position?.y);
  const configuredTextXDots = Number.isFinite(configuredTextX)
    ? Math.round(configuredTextX * TEMPLATE3_DOTS_PER_MM)
    : centeredTextX;
  const textX = Math.max(
    0,
    Math.min(
      TEMPLATE3_LABEL_WIDTH_DOTS,
      getTemplate3LockedX(
        config?.text_config,
        "text",
        text,
        configuredTextXDots,
        fontSize,
      ),
    ),
  );
  // Template 3 uses ^A0I, so a smaller raw Y moves the printed text lower
  // on the physical RFID label.
  const textY = Number.isFinite(configuredTextY)
    ? Math.max(0, Math.min(TEMPLATE3_LABEL_HEIGHT_DOTS, Math.round(configuredTextY * TEMPLATE3_DOTS_PER_MM)))
    : (hasImage ? 70 : 90);

  return `^FO${textX},${textY}^A0I,${fontSize},${fontSize}^FD${text}^FS`;
};



export const template3 = async (selectList, dataList, config = {}) => {
  //console.log(config, 'template 3')
  // eslint-disable-next-line no-useless-escape
  const domain = /:\/\/([^\/]+)/.exec(window.location.href)[1];
  const qrurl = window.location.protocol + "//" + domain + "/v/"

  const allSelectedDataList = getSelectedDataList(selectList, dataList);
  const stock_id = getSelectedDataList(selectList, dataList, "stock_id");
  const product_id = getSelectedDataList(selectList, dataList, "product_id");


  let prnCommandList = []
  let stock_id_list = [];
  function numberWithCommas(x) {
    return x.toString().replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ",");
  }




  let qrCode;
  let info;
  const template3QrEnabled = config?.qr === true || config?.template3_qr === true;

  const yieldToBrowser = () => new Promise(resolve => setTimeout(resolve, 0));
  const generationChunkSize = 10;
  const template3ImageEnabled = isTemplate3OptionEnabled(config?.image) && Boolean(config?.image_config?.data);
  const imageZplCommand = template3ImageEnabled
    ? await getTemplate3ImageCommand(config.image_config)
    : "";
  const textZplCommand = getTemplate3TextCommand(
    config,
    template3ImageEnabled ? config.image_config : null,
  );



  if (template3QrEnabled) {
          // have qr

        dataList.map((v, k) => {
          stock_id_list.push(v.stock_id)
          return true;
        })
                        if (config.qr_type_display === "offline") {

                          qrCode = await _Api({ qrCodeData: stock_id, dataList: allSelectedDataList, templateProfile: config, qr_width: 150, return_type: "final_data" }, "api/v1/qr-code/createofflinetext")


                        } else if (config.qr_type_display === "online") {
                          qrCode = await _Api({ url: config.qr_url_display, qr_width: 150, return_type: "final_data" }, "api/v1/qr-code/createwebsitelink")

                        } else if (config.qr_type_display === "product_page") {
                          info = await _Api({}, `api/v1/store-info/get-db-info`);
                          qrCode = await _Api({ qrCodeData: product_id, dataList: allSelectedDataList, templateProfile: config, website_url: qrurl, info: btoa(info.data), qr_width: 150, return_type: "buffer" }, "api/v1/qr-code/createproductpagelink")
                        } else {
                          qrCode = await _Api({ qrCodeData: stock_id, dataList: allSelectedDataList, templateProfile: config, qr_width: 150, return_type: "buffer" }, "api/v1/qr-code/createofflinetext")
                        }





                      //console.log(qrCode, 'template_03_qr')
                      const fontSizeStoneBylength = {
                        "2": 5,
                        "3": 4,
                        "4": 3,
                        "5": 3
                      }

                      const doc = new jsPDF({
                        orientation: 'landscape',
                        unit: 'mm',
                        format: [75, 13]
                      });

                      //console.log(dataList,'dataList')




                      //  prnCommand += "^XZ"
                      for (let k = 0; k < dataList.length; k += 1) {
                        if (k > 0 && k % generationChunkSize === 0) {
                          await yieldToBrowser();
                        }

                        const v = dataList[k];
                        let prnCommand = "";
                        prnCommand += "^LH0,0";

                        prnCommand += "^LS0";
                        prnCommand += "^PW826";
                        prnCommand += "^LL354";
                        prnCommand += imageZplCommand;
                        prnCommand += textZplCommand;

                        const barcodeDisplayType = String(config.barcode_display || "stock_id").trim().toLowerCase();
                        const barcodeDisplayValue = barcodeDisplayType === "sku" ? v.SKU : v.stock_id;
                        const barcodeTextValue = barcodeDisplayValue || v.stock_id || v.SKU;
                        if (config.barcode === true && barcodeTextValue) {
                          const barcodeTextX = getBarcodeTextX(barcodeTextValue);
                          prnCommand += "^FO" + barcodeTextX + ",125^A0I^BY2^BCN,45,N,N,N^FD" + barcodeTextValue + "^FS";
                        }

                        if (v.SKU) {
                          const skuPosition = getTemplate3FieldPositionDots(config, "sku", getSkuTextX(v.SKU), 173, v.SKU);
                          prnCommand += "^FO" + skuPosition.x + "," + skuPosition.y + "^A0I,27,27^FD" + v.SKU + "^FS";
                        }

                        if (v.stock_id) {
                          const stockIdPosition = getTemplate3FieldPositionDots(config, "stock_id", getLeftColumnTextX(v.stock_id), 93, v.stock_id);
                          prnCommand += "^FO" + stockIdPosition.x + "," + stockIdPosition.y + "^A0I,27,27^FD" + v.stock_id + "^FS";
                        }




                        if (config.metal === true) {
                          // Static test value: const metalDisplayValue  "14K Rose Gold"
                          const metalDisplayValue = getMetalDisplayValue(v.metal_code);
                          if (metalDisplayValue) {   
                            const metalPosition = getTemplate3FieldPositionDots(config, "metal", getMetalTextX(metalDisplayValue), 93, metalDisplayValue);
                            prnCommand += "^FO" + metalPosition.x + "," + metalPosition.y + "^A0I,27,27^FD" + metalDisplayValue + "^FS"


                          }
                        }


                        let numericString;
                        if (isNaN(v.price)) {
                          numericString = v.price.replace(/[^0-9.]/g, '');
                        } else {
                          numericString = v.price;
                        }

                        if (config.price === true) {

                          if (v.price) {

                            if (config.price_display === "with_currency") {
                              if (isNaN(numericString)) {
                                const priceDisplayValue = v.currency + " " + numberWithCommas(parseInt(numericString));
                                const priceTextX = getPriceColumnTextX(priceDisplayValue);
                                const pricePosition = getTemplate3FieldPositionDots(config, "price", priceTextX, 68, priceDisplayValue);
                                prnCommand += "^FO" + pricePosition.x + "," + pricePosition.y + "^A0I,27,27^FD" + priceDisplayValue + "^FS"

                              } else {
                                //console.log('else esle')

                                const priceDisplayValue = v.currency + " " + numberWithCommas(parseInt(numericString));
                                const priceTextX = getPriceColumnTextX(priceDisplayValue);
                                const pricePosition = getTemplate3FieldPositionDots(config, "price", priceTextX, 68, priceDisplayValue);
                                prnCommand += "^FO" + pricePosition.x + "," + pricePosition.y + "^A0I,27,27^FD" + priceDisplayValue + "^FS"

                              }

                            } else {
                              if (isNaN(numericString)) {
                                const priceDisplayValue = applyPriceCode(numberWithCommas(numericString), config, v);
                                const priceTextX = getPriceColumnTextX(priceDisplayValue);
                                const pricePosition = getTemplate3FieldPositionDots(config, "price", priceTextX, 68, priceDisplayValue);
                                prnCommand += "^FO" + pricePosition.x + "," + pricePosition.y + "^A0I,27,27^FD" + priceDisplayValue + "^FS"


                              } else {
                                const priceDisplayValue = applyPriceCode(numberWithCommas(parseFloat(numericString).toFixed(2)), config, v);
                                const priceTextX = getPriceColumnTextX(priceDisplayValue);
                                const pricePosition = getTemplate3FieldPositionDots(config, "price", priceTextX, 68, priceDisplayValue);
                                prnCommand += "^FO" + pricePosition.x + "," + pricePosition.y + "^A0I,27,27^FD" + priceDisplayValue + "^FS"

                              }
                            }



                          }


                        }
                        const nwt = formatWeightValue(v.NetWt) + "g"
                        const gwt = formatWeightValue(v.GrossWt) + "g"
                        const pieceCount = getPrintPieceCount(v);
                        const weightSize = getTemplate3FieldSize(config, "weight", 27);

                        if (config.weight === true) {
                          if (config.weight_display === "nwt") {
                            const weightPosition = getTemplate3FieldPositionDots(config, "weight", getWeightTextX(nwt), 68, nwt, weightSize);
                            prnCommand += "^FO" + weightPosition.x + "," + weightPosition.y + "^A0I," + weightSize + "," + weightSize + "^FD" + nwt + "^FS"
                            if (config.pcs !== false) {
                              prnCommand = appendTemplate3PieceLabel(prnCommand, config, weightPosition, pieceCount);
                            }
                            // doc.text( nwt, 47 ,12, 0);

                          } else {
                            const weightPosition = getTemplate3FieldPositionDots(config, "weight", getWeightTextX(gwt), 68, gwt, weightSize);
                            prnCommand += "^FO" + weightPosition.x + "," + weightPosition.y + "^A0I," + weightSize + "," + weightSize + "^FD" + gwt + "^FS"
                            if (config.pcs !== false) {
                              prnCommand = appendTemplate3PieceLabel(prnCommand, config, weightPosition, pieceCount);
                            }

                            // doc.text( gwt, 47 ,12, 0);

                          }
                        }

                        let stone_list;
                        if (checkType(v.Stones_Data) === "Array") {
                          stone_list = v.Stones_Data
                        } else {
                          stone_list = []
                          stone_list.push(v.Stones_Data)
                        }
                        //console.log(stone_list, 'stone_list')

                        let list_stone = {};
                        const configuredStoneCount = Number(config.preview_stone_count);
                        const stoneCountLimit = Number.isFinite(configuredStoneCount) && configuredStoneCount > 0
                          ? Math.min(5, Math.max(1, Math.floor(configuredStoneCount)))
                          : stone_list.length;

                        stone_list.slice(0, stoneCountLimit).map((v, k, arr) => {

                          // const key = `${config.stone_name&&v.stone_details._id?v.stone_details._id:"no_sc"}_${config.stone_group&&v.stone_group_details._id?v.stone_group_details._id:"no_sg"}_${config.clarity&&v.stone_clarity_details._id?v.stone_clarity_details._id:"no_clar"}_${config.shape&&v.stone_shape_details._id?v.stone_shape_details._id:"no_sh"}`;
                          const key = `${config?.stone_name && v?.stone_details ? v?.stone_details?._id : ""}_${config?.stone_group && v?.stone_group_details ? v?.stone_group_details?._id : ""}_${config?.clarity && v?.stone_clarity_details ? v?.stone_clarity_details?._id : ""}_${config?.shape && v?.stone_shape_details ? v?.stone_shape_details?._id : ""}_${config?.cut && v?.stone_cut_details ? v?.stone_cut_details?._id : ""}_${config?.color && v?.stone_color_details ? v?.stone_color_details?._id : ""}_${config?.stone_size && v?.stone_size_details ? v?.stone_size_details?._id : ""}`;

                          if (!list_stone[key]) {

                            list_stone[key] = { ...v };
                          } else {
                            //console.log(list_stone, 'else ees')
                            // //console.log(merged[key],' merged[key]')
                            // //console.log(merged[key].Cts,' merged[key].Cts')
                            // //console.log(stone.Cts,' stone.Cts')
                            list_stone[key].Cts = parseFloat(parseFloat(list_stone[key].Cts) + parseFloat(v.Cts)).toFixed(2);
                            list_stone[key].Pcs = parseInt(list_stone[key].Pcs) + parseInt(v.Pcs);

                          }
                          return true;
                        })
                        const stone_merge_array = Object.values(list_stone).map(stone => ({
                          ...stone,

                        }));
                        //console.log(stone_merge_array, 'stone_merge_array')
                        // //console.log(getBase64ImageByteCount("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAJYAAACWCAYAAAA8AXHiAAAAAklEQVR4AewaftIAAAO9SURBVO3BQY4cCQ4EQQ+i/v9lX2HO1CGBJKpbG2YB5BdQ2SThCZU3JGGj8kQSNiqbJPwGQ9WBoerAUHVgqDowVB348Bcq35CEJ1S+QeUnUfmGJGyGqgND1YGh6sBQdWCoOvDhoSS8QeUNSdioPJGEb1C5lIQ3qDwxVB0Yqg4MVQeGqgND1YEP/6gkbFSeUKnnhqoDQ9WBoerAUHVgqDrw4f9MEt6QhEsqv9lQdWCoOjBUHRiqDgxVBz48pPKTqGySsFF5IgkblU0SNirfoPINQ9WBoerAUHVgqDowVB348BdJ+A2SsFHZJGGj8g1J2Kg8kYSfZKg6MFQdGKoODFUHhqoD8Q/+QUnYqGySsFHZJOENKv+ioerAUHVgqDowVB0Yqg4EkEMqTyRho7JJwkblUhKeUNkk4QmVJ5LwBpVNEjZD1YGh6sBQdWCoOjBUHYh/sEjCJZUnkrBR2SRho/KTJGGj8kQSNipvSMJGZTNUHRiqDgxVB4aqA0PVgQCyUPmGJGxUfoMkbFTekIQnVL5hqDowVB0Yqg4MVQeGqgMB5AtU3pCEjcoTSfgGlU0SnlB5IglPqDwxVB0Yqg4MVQeGqgND1YEPf6HyhiQ8kYSNykZlk4SNyhMqv1kSNiqbJGyS8MRQdWCoOjBUHRiqDgxVB+IfLJKwUdkkYaNyKQmXVDZJ2KhskrBR2SThksobkrAZqg4MVQeGqgND1YGh6sCHY0n4BpVNEjYqmyRsVDZJ2KhcUtkk4YkkPKGyGaoODFUHhqoDQ9WBoepAAFmobJLwBpUnkvAGlSeS8ITKJglvUNkk4QmVNyRhM1QdGKoODFUHhqoDQ9WB+AeHkvAGlU0SLqlcSsK/aKg6MFQdGKoODFUHhqoDH46p/AYqmyQ8ofIGlTckYaOyScJGZZOEzVB1YKg6MFQdGKoODFUHPkn4DVQ2Kk8kYZOEJ1S+IQkblUtJeGKoOjBUHRiqDgxVB4aqAx/+QuUbknBJ5YkkvCEJb1C5pLJJwhND1YGh6sBQdWCoOjBUHQggC5VNEt6gsknCRuUNSbik8kQSfhKVNwxVB4aqA0PVgaHqwFB14MMvl4Q3qGyS8EQSnlDZJOEJlU0SnkjCG4aqA0PVgaHqwFB1YKg68KH+k4Q3qGySsEnCN6hskvCEymaoOjBUHRiqDgxVB4aqAx8eUvkNVH4SlSeSsFH5BpUnhqoDQ9WBoerAUHVgqDrw4S+S8Jsl4QmVTRI2KpskbFQuJeGJJDyRhCeGqgND1YGh6sBQdWCoOvA/84NYN+nAXAwAAAAASUVORK5CYII="),'stone_merge_array')

                        if (template3QrEnabled) {
                          const currentQrData = Array.isArray(qrCode?.data) ? (qrCode.data[k] || qrCode.data[0]) : qrCode?.data;

                          if (config.qr_type_display === "online") {


                            //console.log(qrCode.data, 'qrCode.data')

                            // const hexData = qrCode.data.toString('hex').toUpperCase();
                            // const bytesPerRow = Math.ceil(94 / 8); // Rounded up as 8 pixels per byte (1-bit per pixel)
                            // ^GFA,4608,12,96,:Z64:<compressed_hex_data>^FS
                            prnCommand += "^FO640,214^BQI,2,4,L^FDLA," + qrCode.data + "^FS"

                            // prnCommand +=  "^FO540,230^BQN,2,10^FD>:"+qrCode.data+"^FS"
                            // prnCommand +=  "^FO540,230^BQN,2,10^FD>:"+qrCode.data+"^FS"

                            // ^FO50,50 ^BQN,2,1^FDLA,Hello, World!^FS


                            //  prnCommand += "^FO540,230"^A0N,25,25^FD"+v.stone_details.code+"^FS"

                          } else if (config.qr_type_display === "offline") {
                            //console.log(qrCode.data[0], 'qrCode.data[0].qrcodeImg')
                            // "offline" bundles multiple product fields into one QR payload, so its length
                            // (and therefore QR version/module count) varies per item, unlike the other
                            // qr_type_display modes. A fixed magnification either clips the symbol off the
                            // label for long payloads or prints unscannably small for short ones, so size it
                            // per item instead.
                            // Keep the known-working automatic QR input format. The newline
                            // representation remains configurable until it can be matched to ERP.
                            const offlineQrSourceText = prepareOfflineQrPayload(currentQrData?.text_qr?.toString() || "");
                            const offlineQrPayloadText = normalizeQrNewlinesForMode(offlineQrSourceText, TEMPLATE3_OFFLINE_QR_NEWLINE_MODE);
                            const offlineQrText = encodeQrFieldDataForZpl(offlineQrSourceText, TEMPLATE3_OFFLINE_QR_NEWLINE_MODE);
                            const offlineQrMagnification = getQrMagnificationForTargetSize(offlineQrPayloadText, QR_TARGET_SIZE_MM, TEMPLATE3_OFFLINE_QR_ERROR_CORRECTION);
                            const offlineQrHexIndicator = TEMPLATE3_OFFLINE_QR_NEWLINE_MODE === "erp_cr" || TEMPLATE3_OFFLINE_QR_NEWLINE_MODE === "erp_lf" ? "\\" : "_";
                            const offlineQrByteLength = new TextEncoder().encode(offlineQrPayloadText).length;
                            const offlineQrPositionY = offlineQrByteLength > TEMPLATE3_OFFLINE_QR_LONG_PAYLOAD_THRESHOLD_BYTES
                              ? TEMPLATE3_OFFLINE_QR_LONG_PAYLOAD_POSITION_Y
                              : TEMPLATE3_OFFLINE_QR_POSITION_Y;
                            prnCommand += "^FO640," + offlineQrPositionY + "^BQI,2," + offlineQrMagnification + "," + TEMPLATE3_OFFLINE_QR_ERROR_CORRECTION + "^FH" + offlineQrHexIndicator + "^FD" + TEMPLATE3_OFFLINE_QR_ERROR_CORRECTION + "A," + offlineQrText + "^FS"
                            //console.log(qrCode.data, 'qrCode.data offline')
                            //console.log(prnCommand, 'prnCommand qrCode.data offline')
                          } else if (config.qr_type_display === "product_page") {
                            //console.log(qrCode.data, 'qrCode.data product_page')
                            //console.log(prnCommand, 'prnCommand qrCode.data product_page')

                            prnCommand += "^FO640,214^BQI,2,4,L^FDLA," + currentQrData?.url + "^FS"
                          } else {
                            prnCommand += "^FO640,230^BQI,2,4,2^FDLA," + qrCode.data + "^FS"

                          }


                        } else if (!template3ImageEnabled) {
                          stone_merge_array.map((v, k, arr) => {

                     

                            let space = 30;
                            let topOffset = 130
                            let position_1 = 750
                            let position_2 = 710
                            let position_3 = 660
                            let position_4 = 580

                            let position_2_s = 675
                            // const text = v.stone_code+" "+v.shape_code+" "+" "+v.Pcs+" "+v.Cts;
                            let space_val = space * (k + 1) + topOffset;
                            if (config.stone_name === true) {
                              if (config.stone_group === true) {
                                prnCommand += "^FO" + position_1 + "," + space_val + "^A0I,25,25^FD" + v.stone_details.code + "^FS"
                                prnCommand += "^FO" + position_2 + "," + space_val + "^A0I,25,25^FD" + v.stone_group_details.code + "^FS"

                              } else {
                                if (config.clarity === false) {

                                  prnCommand += "^FO" + position_1 + "," + space_val + "^A0I,25,25^FD" + v.stone_details.code + "^FS"
                                  // ^FO525,100^A0I,27,27^FD10010^FS
                                  // ^FO690,150^A0I,27,27^FD18KWG^FS
                                  // ^FO525,175^A0I,27,27^FDTHB 0^FS
                                  // ^FO700,175^A0I,27,27^FD G^FS

                                  ///^FO630,190^A0I,27,27^FD18KWG^FS

                                  //30 60 doc.text(v.stone_details.code, 55, space * (k + 1), 0); FO525,230

                                }

                              }

                            } else {
                              if (config.clarity === false) {
                                if (config.stone_group === true) {
                                  prnCommand += "^FO" + position_1 + "," + space_val + "^A0I,25,25^FD" + v.stone_details.code + "^FS"

                                  // doc.text(v.stone_group_details.code, 55, space * (k + 1), 0);

                                }
                              }
                            }

                            if (config.stone_group === true && config.stone_name === false) {
                              prnCommand += "^FO" + position_1 + "," + space_val + "^A0I,25,25^FD" + v.stone_details.code + "^FS"

                              // doc.text(v.stone_details.code, 55, space * (k + 1), 0);



                            }




                            if (config.clarity === true && config.stone_group === false && config.stone_name === false) {
                              if (v.clarity) {
                                prnCommand += "^FO" + position_2 + "," + space_val + "^A0I,25,25^FD" + v.stone_clarity_details.code + "^FS"
                                // doc.text(v.stone_clarity_details.code, 60, space * (k + 1),);


                              }

                            }

                            if (v.shape) {
                              if (config.shape === true) {
                                if (config.clarity === true) {
                                  prnCommand += "^FO" + position_1 + "," + space_val + "^A0I,25,25^FD" + (v.stone_shape_details?.code || "") + "^FS"
                                  // doc.text(v.stone_shape_details.code, 55, space * (k + 1),);

                                } else {

                                  if (config.stone_group === false && config.stone_name === false) {
                                    prnCommand += "^FO" + position_1 + "," + space_val + "^A0I,25,25^FD" + (v.stone_shape_details?.code || "") + "^FS"

                                    // doc.text(v.stone_shape_details.code,55, space * (k + 1),);
                                  }
                                  if (config.stone_group === false && config.stone_name === true) {
                                    prnCommand += "^FO" + position_2 + "," + space_val + "^A0I,25,25^FD" + (v.stone_shape_details?.code || "") + "^FS"

                                    // doc.text(v.stone_shape_details.code,55, space * (k + 1),);
                                  }
                                }
                              }



                            }


                            if (v.color) {
                              if (config.color === true) {
                                if (v.stone_color_details) {
                                  prnCommand += "^FO" + position_2 + "," + space_val + "^A0I,25,25^FD" + v.stone_color_details.code + "^FS"

                                }

                              }
                            }


                            if (v.Cut) {
                              if (config.cut === true) {
                                if (v.stone_cut_details) {
                                  prnCommand += "^FO" + position_2 + "," + space_val + "^A0I,25,25^FD" + v.stone_cut_details.code + "^FS"

                                  // doc.text(v.stone_cut_details.code, 60, space * (k + 1),);

                                }
                              }
                            }



                            if (v.Size) {
                              if (config.stone_size === true) {
                                prnCommand += "^FO" + position_2_s + "," + space_val + "^A0I,25,25^FD" + (v.stone_size_details?.code || v.size_name || "") + "^FS"

                                // doc.text(v.stone_size_details.code?v.stone_size_details.code:"", 58, space * (k + 1),);
                              }

                            }

                            if (v.Pcs) {

                              if (config.stone_pcs === true) {
                                prnCommand += "^FO" + position_3 + "," + space_val + "^A0I,25,25^FD" + v.Pcs + "^FS";
                                // doc.text(v.Pcs.toString(), 65.5, space * (k + 1));

                              }

                            }

                            if (config.stone_weight === true) {
                              prnCommand += "^FO" + position_4 + "," + space_val + "^A0I,25,25^FD" + v.Cts.toString() + v.Unit.substring(0, 1).toLowerCase() + "^FS"
                              // doc.text(v.Cts.toString() + v.Unit.substring(0, 1).toLowerCase(), 69, space * (k + 1),);

                            }
                            return true;
                          })
                        }


                        // if (k + 1 < dataList.length) {
                        //   doc.addPage();

                        // }
                        prnCommand += "^PQ1,0,1,Y"

                        // Disable RFID read/write commands when printing on non-RFID labels.
                        // RFID printers mark labels as VOID when tag operations fail on media without chips.
                        prnCommand += "^FN2^RFR,H,2,16,1^FS"
                        prnCommand += "^RFW,H,1,2,1^FD3800^FS"
                        prnCommand += "^RFW,H,2,8,1^FD" + encodeToHex(v.stock_id) + "^FS"

                        prnCommandList.push(prnCommand)

                      }

                      //console.log(prnCommand, 'prnCommand.length')
                      return prnCommandList;

        } else {

                //no qr code
                for (let k = 0; k < dataList.length; k += 1) {
                  if (k > 0 && k % generationChunkSize === 0) {
                    await yieldToBrowser();
                  }

                  const v = dataList[k];

                  let prnCommand = "";
                  prnCommand += "^LH0,0";
              
                  prnCommand += "^LS0";
                  prnCommand += "^PW826";
                  prnCommand += "^LL354";
                  prnCommand += imageZplCommand;
                  prnCommand += textZplCommand;
              
                  // SKU-through-weight block shifted up 0.5mm (~6 dots at 300dpi) per a real
                  // print (no-QR layout only - the QR layout's copy of this block is untouched).
                  // Pulled back down from 327: that left only 27 dots of clearance to ^LL354's
                  // top edge, exactly the field's own font height (27,27), so it clipped.
                  // X was a fixed 555 regardless of SKU length, so - same root cause as the
                  // QR layout's original SKU bug - it drifted off the intended left edge for
                  // any SKU shorter/longer than whatever length 555 happened to suit. Now
                  // uses the same dynamic getSkuTextX measurement as the QR layout's SKU.
                  const noQrSkuPosition = getTemplate3FieldPositionDots(config, "sku", getSkuTextX(v.SKU), 320, v.SKU);
                  prnCommand += "^FO" + noQrSkuPosition.x + "," + noQrSkuPosition.y + "^A0I,27,27^FD" + v.SKU + "^FS";

                  const barcodeDisplayType = String(config.barcode_display || "stock_id").trim().toLowerCase();
                  const barcodeDisplayValue = barcodeDisplayType === "sku" ? v.SKU : v.stock_id;
                  const barcodeTextValue = barcodeDisplayValue || v.stock_id || v.SKU;


                  if (config.barcode === true && barcodeTextValue) {
                    const barcodeTextX = getBarcodeTextX(barcodeTextValue);
                    prnCommand += "^FO" + barcodeTextX + ",276^A0I^BY2^BCN,45,N,N,N^FD" + barcodeTextValue + "^FS";

                  }
                  if (v.stock_id) {
                    const stockIdPosition = getTemplate3FieldPositionDots(config, "stock_id", getLeftColumnTextX(v.stock_id), 247, v.stock_id);
                    prnCommand += "^FO" + stockIdPosition.x + "," + stockIdPosition.y + "^A0I,27,27^FD" + v.stock_id + "^FS";
                  }

              
              
                  if (config.metal === true) {
                    // Static test value: const metalDisplayValue  "14K Rose Gold"
                    const metalDisplayValue = getMetalDisplayValue(v.metal_code);
                    if (metalDisplayValue) {   
                      const metalPosition = getTemplate3FieldPositionDots(config, "metal", getMetalTextX(metalDisplayValue), 247, metalDisplayValue);
                      prnCommand += "^FO" + metalPosition.x + "," + metalPosition.y + "^A0I,27,27^FD" + metalDisplayValue + "^FS"


                    }
                  }

              
              
                  let numericString;
                  if (isNaN(v.price)) {
                    numericString = v.price.replace(/[^0-9.]/g, '');
                  } else {
                    numericString = v.price;
                  }
              
                  if (config.price === true) {
              
                    if (v.price) {

                      if (config.price_display === "with_currency") {
                        if (isNaN(numericString)) {
                          const priceDisplayValue = v.currency + " " + numberWithCommas(parseInt(numericString));
                          const priceTextX = getPriceColumnTextX(priceDisplayValue);
                          const pricePosition = getTemplate3FieldPositionDots(config, "price", priceTextX, 222, priceDisplayValue);
                          prnCommand += "^FO" + pricePosition.x + "," + pricePosition.y + "^A0I,27,27^FD" + priceDisplayValue + "^FS"

                        } else {
                          //console.log('else esle')

                          const priceDisplayValue = v.currency + " " + numberWithCommas(parseInt(numericString));
                          const priceTextX = getPriceColumnTextX(priceDisplayValue);
                          const pricePosition = getTemplate3FieldPositionDots(config, "price", priceTextX, 222, priceDisplayValue);
                          prnCommand += "^FO" + pricePosition.x + "," + pricePosition.y + "^A0I,27,27^FD" + priceDisplayValue + "^FS"

                        }

                      } else {
                        if (isNaN(numericString)) {
                          const priceDisplayValue = applyPriceCode(numberWithCommas(numericString), config, v);
                          const priceTextX = getPriceColumnTextX(priceDisplayValue);
                          const pricePosition = getTemplate3FieldPositionDots(config, "price", priceTextX, 222, priceDisplayValue);
                          prnCommand += "^FO" + pricePosition.x + "," + pricePosition.y + "^A0I,27,27^FD" + priceDisplayValue + "^FS"


                        } else {
                          const priceDisplayValue = applyPriceCode(numberWithCommas(parseFloat(numericString).toFixed(2)), config, v);
                          const priceTextX = getPriceColumnTextX(priceDisplayValue);
                          const pricePosition = getTemplate3FieldPositionDots(config, "price", priceTextX, 222, priceDisplayValue);
                          prnCommand += "^FO" + pricePosition.x + "," + pricePosition.y + "^A0I,27,27^FD" + priceDisplayValue + "^FS"

                        }
                      }



                    }
              
              
                  }
                  const nwt = formatWeightValue(v.NetWt) + "g"
                  const gwt = formatWeightValue(v.GrossWt) + "g"
                  const pieceCount = getPrintPieceCount(v);
                  const weightSize = getTemplate3FieldSize(config, "weight", 27);

                  if (config.weight === true) {
                    if (config.weight_display === "nwt") {
                      const weightPosition = getTemplate3FieldPositionDots(config, "weight", getWeightTextX(nwt), 222, nwt, weightSize);
                      prnCommand += "^FO" + weightPosition.x + "," + weightPosition.y + "^A0I," + weightSize + "," + weightSize + "^FD" + nwt + "^FS"
                      if (config.pcs !== false) {
                        prnCommand = appendTemplate3PieceLabel(prnCommand, config, weightPosition, pieceCount);
                      }
                      // doc.text( nwt, 47 ,12, 0);

                    } else {
                      const weightPosition = getTemplate3FieldPositionDots(config, "weight", getWeightTextX(gwt), 222, gwt, weightSize);
                      prnCommand += "^FO" + weightPosition.x + "," + weightPosition.y + "^A0I," + weightSize + "," + weightSize + "^FD" + gwt + "^FS"
                      if (config.pcs !== false) {
                        prnCommand = appendTemplate3PieceLabel(prnCommand, config, weightPosition, pieceCount);
                      }

                      // doc.text( gwt, 47 ,12, 0);

                    }
                  }
              
                  let stone_list;
                  if (checkType(v.Stones_Data) === "Array") {
                    stone_list = v.Stones_Data
                  } else {
                    stone_list = []
                    stone_list.push(v.Stones_Data)
                  }
                  ////console.log(stone_list, 'stone_list')
              
                  let list_stone = {};
                  const configuredStoneCount = Number(config.preview_stone_count);
                  const stoneCountLimit = Number.isFinite(configuredStoneCount) && configuredStoneCount > 0
                    ? Math.min(5, Math.max(1, Math.floor(configuredStoneCount)))
                    : stone_list.length;
              
                  stone_list.slice(0, stoneCountLimit).map((v, k, arr) => {
              
                    // const key = `${config.stone_name&&v.stone_details._id?v.stone_details._id:"no_sc"}_${config.stone_group&&v.stone_group_details._id?v.stone_group_details._id:"no_sg"}_${config.clarity&&v.stone_clarity_details._id?v.stone_clarity_details._id:"no_clar"}_${config.shape&&v.stone_shape_details._id?v.stone_shape_details._id:"no_sh"}`;
                    const key = `${config.stone_name && v.stone_details ? v.stone_details._id : ""}_${config.stone_group && v.stone_group_details ? v.stone_group_details._id : ""}_${config.clarity && v.stone_clarity_details ? v.stone_clarity_details._id : ""}_${config.shape && v.stone_shape_details ? v.stone_shape_details._id : ""}_${config.cut && v.stone_cut_details ? v.stone_cut_details._id : ""}_${config.color && v.stone_color_details ? v.stone_color_details._id : ""}_${config.stone_size && v.stone_size_details ? v.stone_size_details._id : ""}`;
              
                    if (!list_stone[key]) {
              
                      list_stone[key] = { ...v };
                    } else {
                      //console.log(list_stone, 'else ees')
                      // //console.log(merged[key],' merged[key]')
                      // //console.log(merged[key].Cts,' merged[key].Cts')
                      // //console.log(stone.Cts,' stone.Cts')
                      list_stone[key].Cts = parseFloat(parseFloat(list_stone[key].Cts) + parseFloat(v.Cts)).toFixed(2);
                      list_stone[key].Pcs = parseInt(list_stone[key].Pcs) + parseInt(v.Pcs);
              
                    }
                    return true;
                  })
                  const stone_merge_array = Object.values(list_stone).map(stone => ({
                    ...stone,
              
                  }));
                  ////console.log(stone_merge_array, 'stone_merge_array')
                  // //console.log(getBase64ImageByteCount("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAJYAAACWCAYAAAA8AXHiAAAAAklEQVR4AewaftIAAAO9SURBVO3BQY4cCQ4EQQ+i/v9lX2HO1CGBJKpbG2YB5BdQ2SThCZU3JGGj8kQSNiqbJPwGQ9WBoerAUHVgqDowVB348Bcq35CEJ1S+QeUnUfmGJGyGqgND1YGh6sBQdWCoOvDhoSS8QeUNSdioPJGEb1C5lIQ3qDwxVB0Yqg4MVQeGqgND1YEP/6gkbFSeUKnnhqoDQ9WBoerAUHVgqDrw4f9MEt6QhEsqv9lQdWCoOjBUHRiqDgxVBz48pPKTqGySsFF5IgkblU0SNirfoPINQ9WBoerAUHVgqDowVB348BdJ+A2SsFHZJGGj8g1J2Kg8kYSfZKg6MFQdGKoODFUHhqoD8Q/+QUnYqGySsFHZJOENKv+ioerAUHVgqDowVB0Yqg4EkEMqTyRho7JJwkblUhKeUNkk4QmVJ5LwBpVNEjZD1YGh6sBQdWCoOjBUHYh/sEjCJZUnkrBR2SRho/KTJGGj8kQSNipvSMJGZTNUHRiqDgxVB4aqA0PVgQCyUPmGJGxUfoMkbFTekIQnVL5hqDowVB0Yqg4MVQeGqgMB5AtU3pCEjcoTSfgGlU0SnlB5IglPqDwxVB0Yqg4MVQeGqgND1YEPf6HyhiQ8kYSNykZlk4SNyhMqv1kSNiqbJGyS8MRQdWCoOjBUHRiqDgxVB+IfLJKwUdkkYaNyKQmXVDZJ2KhskrBR2SThksobkrAZqg4MVQeGqgND1YGh6sCHY0n4BpVNEjYqmyRsVDZJ2KhcUtkk4YkkPKGyGaoODFUHhqoDQ9WBoepAAFmobJLwBpUnkvAGlSeS8ITKJglvUNkk4QmVNyRhM1QdGKoODFUHhqoDQ9WB+AeHkvAGlU0SLqlcSsK/aKg6MFQdGKoODFUHhqoDH46p/AYqmyQ8ofIGlTckYaOyScJGZZOEzVB1YKg6MFQdGKoODFUHPkn4DVQ2Kk8kYZOEJ1S+IQkblUtJeGKoOjBUHRiqDgxVB4aqAx/+QuUbknBJ5YkkvCEJb1C5pLJJwhND1YGh6sBQdWCoOjBUHQggC5VNEt6gsknCRuUNSbik8kQSfhKVNwxVB4aqA0PVgaHqwFB14MMvl4Q3qGyS8EQSnlDZJOEJlU0SnkjCG4aqA0PVgaHqwFB1YKg68KH+k4Q3qGySsEnCN6hskvCEymaoOjBUHRiqDgxVB4aqAx8eUvkNVH4SlSeSsFH5BpUnhqoDQ9WBoerAUHVgqDrw4S+S8Jsl4QmVTRI2KpskbFQuJeGJJDyRhCeGqgND1YGh6sBQdWCoOvA/84NYN+nAXAwAAAAASUVORK5CYII="),'stone_merge_array')
               
                  if (!template3ImageEnabled) stone_merge_array.map((v, k, arr) => {
              
              
                      // ~2mm line height (24 dots at 300dpi) instead of the old 30 dots
                      // (~2.5mm), so 5 stone rows land closer to the bottom edge without
                      // clipping it.
                      let space = 24;
                      // firstRowY is the k=0 (first stone) Y position directly - measured
                      // against a real print: the first row printed 3.5mm (~41 dots at this
                      // printer's confirmed 300dpi) below the marked target line, so shifted
                      // up by that amount from the original 140 (110 + the old 30 line
                      // height) to 181. Kept independent of `space` so tuning line height
                      // doesn't also nudge this already-confirmed anchor point.
                      let firstRowY = 181;
                      // 0.5cm was too much - back to 740 (the "-1 char right" value) +1mm left (12 dots).
                      let position_1 = 752
                      // Same: 676 +1mm left (12 dots).
                      let position_2 = 688
                      // -2mm right (24 dots at 300dpi), +1mm left (12 dots) more.
                      let position_3 = 638
                      // -4mm right (47 dots at 300dpi).
                      let position_4 = 523

                      let position_2_s = 675
                      // const text = v.stone_code+" "+v.shape_code+" "+" "+v.Pcs+" "+v.Cts;
                      // Label prints via ^A0I (180deg), so - like every X position on this
                      // label - larger Y is further UP, not down. The old "+ space*(k+1)"
                      // pushed each additional stone row further UP the label, straight into
                      // the barcode/metal/price/weight block above (confirmed against a real
                      // print: 5 stones overlapped that block, while a single-stone print sat
                      // fine at the k=0 position). Stones now grow DOWN into the free space
                      // below that first row instead, keeping k=0 at the same confirmed-good
                      // spot.
                      let space_val = firstRowY - space * k;
                      if (config.stone_name === true) {
                        if (config.stone_group === true) {
                          prnCommand += "^FO" + position_1 + "," + space_val + "^A0I,25,25^FD" + v.stone_details.code + "^FS"
                          prnCommand += "^FO" + position_2 + "," + space_val + "^A0I,25,25^FD" + v.stone_group_details.code + "^FS"
              
                        } else {
                          if (config.clarity === false) {
              
                            prnCommand += "^FO" + position_1 + "," + space_val + "^A0I,25,25^FD" + v.stone_details.code + "^FS"
              
                          }
              
                        }
              
                      } else {
                        if (config.clarity === false) {
                          if (config.stone_group === true) {
                            prnCommand += "^FO" + position_1 + "," + space_val + "^A0I,25,25^FD" + v.stone_details.code + "^FS"
              
              
                          }
                        }
                      }
              
                      if (config.stone_group === true && config.stone_name === false) {
                        prnCommand += "^FO" + position_1 + "," + space_val + "^A0I,25,25^FD" + v.stone_details.code + "^FS"
              
              
              
              
                      }
              
              
              
              
                      if (config.clarity === true && config.stone_group === false && config.stone_name === false) {
                        if (v.clarity) {
                          prnCommand += "^FO" + position_2 + "," + space_val + "^A0I,25,25^FD" + v.stone_clarity_details.code + "^FS"
              
              
                        }
              
                      }
              
                      if (v.shape) {
                        if (config.shape === true) {
                          if (config.clarity === true) {
                            prnCommand += "^FO" + position_1 + "," + space_val + "^A0I,25,25^FD" + (v.stone_shape_details?.code || "") + "^FS"
              
                          } else {
              
                            if (config.stone_group === false && config.stone_name === false) {
                              prnCommand += "^FO" + position_1 + "," + space_val + "^A0I,25,25^FD" + (v.stone_shape_details?.code || "") + "^FS"
              
                            }
                            if (config.stone_group === false && config.stone_name === true) {
                              prnCommand += "^FO" + position_2 + "," + space_val + "^A0I,25,25^FD" + (v.stone_shape_details?.code || "") + "^FS"
              
                            }
                          }
                        }
              
              
              
                      }
              
              
                      if (v.color) {
                        if (config.color === true) {
                          if (v.stone_color_details) {
                            prnCommand += "^FO" + position_2 + "," + space_val + "^A0I,25,25^FD" + v.stone_color_details.code + "^FS"
              
                          }
              
                        }
                      }
              
              
                      if (v.Cut) {
                        if (config.cut === true) {
                          if (v.stone_cut_details) {
                            prnCommand += "^FO" + position_2 + "," + space_val + "^A0I,25,25^FD" + v.stone_cut_details.code + "^FS"
              
              
                          }
                        }
                      }
              
              
              
                      if (v.Size) {
                        if (config.stone_size === true) {
                          prnCommand += "^FO" + position_2_s + "," + space_val + "^A0I,25,25^FD" + (v.stone_size_details?.code || v.size_name || "") + "^FS"
              
                        }
              
                      }
              
                      if (v.Pcs) {
              
                        if (config.stone_pcs === true) {
                          prnCommand += "^FO" + position_3 + "," + space_val + "^A0I,25,25^FD" + v.Pcs + "^FS";
              
                        }
              
                      }
              
                      if (config.stone_weight === true) {
                        prnCommand += "^FO" + position_4 + "," + space_val + "^A0I,25,25^FD" + truncateToTwoDecimals(v.Cts) + v.Unit.substring(0, 1).toLowerCase() + "^FS"

                      }
                      return true;
                    })
                  
              
                  prnCommand += "^PQ1,0,1,Y"

                  // Disable RFID read/write commands when printing on non-RFID labels.
                  // RFID printers mark labels as VOID when tag operations fail on media without chips.
                  prnCommand += "^FN2^RFR,H,2,16,1^FS"
                  prnCommand += "^RFW,H,1,2,1^FD3800^FS"
                  prnCommand += "^RFW,H,2,8,1^FD" + encodeToHex(v.stock_id) + "^FS"

              
                  prnCommandList.push(prnCommand)
              
                }
              
                //console.log(prnCommand, 'prnCommand.length')
                return prnCommandList;
              }


}


